import { Check } from 'lucide-react'
import { cn } from '../../lib/cn'

interface StepperProps {
  steps: ReadonlyArray<{ id: string; label: string }>
  activeIndex: number
  className?: string
  /** Accessible name for the step list (e.g. "Session steps"). */
  label?: string
}

export function Stepper({ steps, activeIndex, className, label = 'Steps' }: StepperProps) {
  return (
    <ol className={cn('flex items-center gap-2', className)} aria-label={label}>
      {steps.map((step, i) => {
        const state = i < activeIndex ? 'done' : i === activeIndex ? 'active' : 'upcoming'
        return (
          <li key={step.id} className="flex flex-1 items-center gap-2">
            <span
              aria-current={state === 'active' ? 'step' : undefined}
              className={cn(
                'inline-flex items-center gap-2 rounded-chip px-3 py-1.5 text-sm font-medium transition',
                // /60 ring (not §6.0's literal /40): /40 composites ~2.7:1 on
                // canvas — under the 3:1 non-text boundary (WCAG 1.4.11); /60
                // matches the §6.4 selection-ring opacity used by chips.
                state === 'active' && 'bg-ember-500/15 text-ember-300 ring-1 ring-ember-400/60',
                state === 'done' && 'text-text',
                // muted, not faint: upcoming step names are real content (WCAG 1.4.3).
                state === 'upcoming' && 'text-text-muted',
              )}
            >
              <span
                className={cn(
                  'inline-flex h-5 w-5 items-center justify-center rounded-full text-xs',
                  state === 'active' && 'bg-ember-400 text-text-on-ember',
                  state === 'done' && 'text-success-fg',
                  state === 'upcoming' && 'ring-1 ring-border',
                )}
                aria-hidden="true"
              >
                {state === 'done' ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : i + 1}
              </span>
              {step.label}
            </span>
            {i < steps.length - 1 && (
              // Connector: traversed portion fills warm (§6.0 bg-ember-700) —
              // 2px so the progress read survives a glance on the dark canvas.
              <span
                aria-hidden="true"
                className={cn('h-0.5 flex-1 rounded-full', i < activeIndex ? 'bg-ember-700' : 'bg-border')}
              />
            )}
          </li>
        )
      })}
    </ol>
  )
}
